import type { Timestamp } from 'firebase/firestore'

export interface ColorPreferences {
  primary?: string
  secondary?: string
  accent?: string
  // Deprecated legacy key (kept for backward compatibility with older user docs)
  tertiary?: string
  background?: string
  // Dark mode specific
  darkBackground?: string
  logoLightText?: string
  logoDarkText?: string
  [key: string]: string | undefined
}

export interface FontPreferences {
  headerFont?: string
  bodyFont?: string
  fontSize?: number
}

export interface ThemePreferences {
  darkMode?: boolean
  colors?: ColorPreferences
  fonts?: FontPreferences
}

// Membership Tiers - ordered from highest to lowest privilege
export type MembershipTier = 'creator' | 'founding_member' | 'pro_member' | 'member' | 'free'

export interface TierConfig {
  id: MembershipTier
  displayName: string
  shortName: string  // For chips/badges
  icon: string
  color: string
  description: string
  monthlyCredits: number
  creditsCap: number
  hasFullAccess: boolean
}

// Tier configuration - extensible for future tiers
export const MEMBERSHIP_TIERS: Record<MembershipTier, TierConfig> = {
  creator: {
    id: 'creator',
    displayName: 'Creator',
    shortName: 'Creator',
    icon: 'mdi-crown',
    color: 'purple',
    description: 'App creators with full access',
    monthlyCredits: 100,
    creditsCap: 600,
    hasFullAccess: true
  },
  founding_member: {
    id: 'founding_member',
    displayName: 'Founding Member',
    shortName: 'Founder',
    icon: 'mdi-medal',
    color: 'amber-darken-2',
    description: 'Early adopters who helped shape Shelf',
    monthlyCredits: 100,
    creditsCap: 600,
    hasFullAccess: true
  },
  pro_member: {
    id: 'pro_member',
    displayName: 'Premium Member',
    shortName: 'Premium',
    icon: 'mdi-star',
    color: 'primary',
    description: 'Premium access with enhanced AI features',
    monthlyCredits: 100,
    creditsCap: 600,
    hasFullAccess: true
  },
  member: {
    id: 'member',
    displayName: 'Premium Member',
    shortName: 'Premium',
    icon: 'mdi-account-check',
    color: 'success',
    description: 'Premium access to Shelf features',
    monthlyCredits: 100,
    creditsCap: 600,
    hasFullAccess: true
  },
  free: {
    id: 'free',
    displayName: 'Free User',
    shortName: 'Free',
    icon: 'mdi-account',
    color: 'grey',
    description: 'Core Shelf features at no cost',
    monthlyCredits: 100,
    creditsCap: 100,
    hasFullAccess: false
  }
}

// Creator user IDs - hardcoded for special access
export const CREATOR_USER_IDS = [
  'LleZY4M6ssPiqdXXwObEJwlXGjh2',  // Jordan
  'AwaJ0ftsE0PM8OGqdQ4IRietHp53'   // Nasa
]

// Founding member cutoff date
export const FOUNDING_MEMBER_CUTOFF = new Date('2026-04-01T00:00:00Z')

// Subscription types
export type SubscriptionStatus = 'free' | 'active' | 'canceled' | 'past_due' | 'gift'
export type SubscriptionPlan = 'monthly' | 'annual' | 'gift' | null

export interface UserSubscription {
  status: SubscriptionStatus
  plan: SubscriptionPlan
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  currentPeriodEnd?: Timestamp
  cancelAtPeriodEnd?: boolean
  // Gift/promo subscription fields
  giftedBy?: string // Admin user ID who granted the gift
  giftReason?: string // "Prize", "Beta Tester", "Referral Reward", etc.
  giftExpiresAt?: Timestamp // Optional expiration for gift subscriptions
}

export interface UserProfile {
  uid: string
  name?: string | null
  email?: string | null
  image_url?: string | null
  current_pantry?: string | null
  current_household?: string | null  // Currently selected household
  colors?: ColorPreferences | null
  darkMode?: boolean // User's dark mode preference
  fonts?: FontPreferences | null // User's font preferences
  pantries?: string[] // Array of pantry IDs the user belongs to
  households?: string[] // Array of household IDs the user belongs to
  recipe_books?: string[] // Array of recipe book IDs the user belongs to
  favorite_recipes?: string[] // Array of favorite recipe IDs
  shopping_list?: ShoppingListItem[] // User's custom shopping list
  subscription?: UserSubscription // Subscription status
  pantry_item_optional_data_panel?: boolean
  pantry_item_form_complexity_default?: 'simple' | 'advanced' | string
  show_pantry_item_notes?: boolean
  show_pantry_item_expiration_date?: boolean
  show_pantry_item_date_added?: boolean
  show_pantry_item_price?: boolean
  show_pantry_item_total_calories?: boolean
  show_pantry_item_brand?: boolean
  show_pantry_item_location?: boolean
  [key: string]: unknown
}

export interface PantryItemAlias {
  name: string
}

export type PantryItemStatus = 'in_stock' | 'need_more' | 'out_of_stock' | 'not_tracking'

export interface PantryItem {
  id?: string
  name: string
  displayName?: string
  quantity?: number
  measurement?: string
  status?: PantryItemStatus | string
  formComplexity?: 'simple' | 'advanced' | number
  aliases?: PantryItemAlias[]
  measurementUnits?: string[]
  category?: string
  statusChangeCount?: number // Track frequency of status changes
  updated_at?: Date | { seconds: number; nanoseconds: number }
  created_at?: Date | { seconds: number; nanoseconds: number }
  [key: string]: unknown
}

export interface PantryMember {
  id: string
  name?: string | null
  email?: string | null
  image_url?: string | null
  role?: string | null
}

export interface Pantry {
  id?: string
  name: string
  member_count?: number
  members?: PantryMember[]
  pantryItems?: PantryItem[]
  [key: string]: unknown
}

export interface RecipeIngredient {
  name: string
  quantity?: number | string
  measurement?: string
  measurements?: Record<string, unknown>
  // For intermediate outputs from previous steps
  isOutput?: boolean  // This ingredient is produced by a previous step
  fromStep?: number   // Which step produces this ingredient
}

export interface RecipeDirection {
  text: string
  ingredients?: RecipeIngredient[]  // Ingredients used in this step
  produces?: RecipeIngredient       // What this step produces (e.g., "dough", "mixture")
  producesOutput?: boolean          // Whether this step produces an output for later steps
  outputName?: string               // Name of the output (e.g., "marinated chicken")
  imageUrl?: string                 // AI-generated or uploaded image for this step
  imageRef?: string                 // Storage reference for the image
}

export interface RecipeTag {
  name: string
}

export interface Recipe {
  id?: string
  name: string
  description?: string | null
  owner?: string
  image_url?: string | null
  image_ref?: string | null
  discoverable?: boolean
  recipe_book_ref_count?: number
  orphaned_at?: Timestamp | null
  searchableName?: string[]
  ingredients?: RecipeIngredient[]
  directions?: RecipeDirection[]
  tags?: RecipeTag[]
  updated_at?: Timestamp
  created_at?: Timestamp
  // Clone tracking
  cloned_from?: string | null  // ID of the original recipe this was cloned from
  cloned_at?: Timestamp | null  // When the clone was made
  ai_candidates?: {
    jobId: string
    userId: string
    imageUrl: string
    imageRef: string
    type: string
    created_at: Timestamp | Date
  }[]
  [key: string]: unknown
}

export interface RecipeBookMember {
  id: string
  role?: string
  name?: string | null
  email?: string | null
  image_url?: string | null
}

export interface RecipeBookRecipePage {
  id?: string
  ref?: string
  [key: string]: unknown
}

export interface RecipeBook {
  id?: string
  name: string
  purpose?: string | null
  cover_image_url?: string | null
  cover_image_ref?: string | null
  recipe_count?: number
  member_count?: number
  members?: RecipeBookMember[]
  recipes?: Recipe[]
  docsIndex?: number
  householdId?: string | null  // If set, this recipe book is shared with the household
  [key: string]: unknown
}

export interface ShoppingListItem {
  name: string
  measurements?: Record<string, unknown>
  checked?: boolean
}

// Meal Planning Types
export type MealSlotKind = 'specific' | 'random' | 'eating_out'
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export interface MealSlot {
  id: string
  name: string
  kind: MealSlotKind
  time?: string | null  // Local wall-clock time in HH:mm; null means unscheduled
  // For 'specific' kind
  recipeId?: string | null
  // For 'random' kind
  includeIngredients?: string[]
  includePublic?: boolean
  pickedRecipeId?: string | null
  // For 'eating_out' kind
  restaurantName?: string | null
  restaurantProvider?: 'google_places' | 'manual' | null
  restaurantId?: string | null
  restaurantLogoUrl?: string | null
}

export interface MealPlanDateOverride {
  cancellations: string[]  // Slot IDs to cancel for this date
  additions: MealSlot[]    // Additional meals for this date
}

export interface MealPlan {
  id?: string
  householdId: string
  recurring: Record<Weekday, MealSlot[]>
  dateOverrides: Record<string, MealPlanDateOverride>  // ISO date string keys
  created_at?: Timestamp
  updated_at?: Timestamp
}

export interface SavedRestaurant {
  id?: string
  householdId: string
  provider: 'google_places' | 'manual'
  providerPlaceId?: string | null
  name: string
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  logoUrl?: string | null
  websiteUrl?: string | null
  created_at?: Timestamp
  updated_at?: Timestamp
}

// Household Types
export interface HouseholdMember {
  uid: string
  name?: string | null
  email?: string | null
  image_url?: string | null
  role: 'owner' | 'member'
  joined_at?: Timestamp | Date  // Date used when creating (can't use serverTimestamp in arrays)
}

export interface Household {
  id?: string
  name: string
  ownerUid: string
  members: HouseholdMember[]
  memberIds?: string[] // Array of UIDs for security rules
  pantryIds: string[]
  mealPlanId: string
  recipeBookIds?: string[]  // Recipe books shared at household level
  shoppingList?: ShoppingListItem[]
  inviteCode?: string  // For easy joining
  // Soft delete fields
  deleted?: boolean           // True if marked for deletion
  deletedAt?: Timestamp       // When deletion was initiated
  deletedBy?: string          // UID of user who initiated deletion
  // Timestamps
  created_at?: Timestamp
  updated_at?: Timestamp
}
